import { useCallback, useEffect, useRef, useState } from "react"

import { useIsMountedRef } from "./useIsMounted"

export const useDisableTabbing = (disabled: boolean) => {
  const onTab = useCallback(
    (event: KeyboardEvent) => {
      if (disabled && event.key === "Tab") {
        event.preventDefault()
      }
    },
    [disabled],
  )
  useEffect(() => {
    document.addEventListener("keydown", onTab)
    return () => {
      document.removeEventListener("keydown", onTab)
    }
  })
}

/* When animationTrigger toggles between true <-> false,
 * disable tabbing for animationDurationMs milliseconds.
 *
 * Use case: to avoid tabbing to something during animation
 * we use this for a few drawer animations at the moment
 */
export const useDisableTabbingDuringAnimation = (
  animationTrigger: boolean,
  animationDurationMs: number,
) => {
  const isMountedRef = useIsMountedRef()
  const previousAnimationTriggerRef = useRef(animationTrigger)
  const [isTabbingDisabled, setIsTabbingDisabled] = useState(false)

  // For some reason, about 100 extra ms are needed for this hook to properly
  // avoid tabbing to something that becomes `visibility: hidden` after animationDurationMs
  const millisecondsToDisable = animationDurationMs + 100

  useEffect(() => {
    if (previousAnimationTriggerRef.current !== animationTrigger) {
      setIsTabbingDisabled(true)
      setTimeout(() => {
        if (isMountedRef.current) {
          setIsTabbingDisabled(false)
        }
      }, millisecondsToDisable)
    }
    previousAnimationTriggerRef.current = animationTrigger
  }, [animationTrigger])
  useDisableTabbing(isTabbingDisabled)
}
